import React from 'react';
import Link from 'next/link';
import DoubleHeader from 'components/DoubleHeader';
import NextSteps from 'components/NextSteps';
import SalesCalloutHTO from 'components/SalesCalloutHTO';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import Line from 'components/Line';
import FaqItem from 'components/FaqItem';
import FaqGroup from 'components/FaqGroup';
import { withPageStaticProps } from 'utils/header';
import { HTO_RETURN_WINDOW_IN_HOURS } from 'data/constants';

class FAQ extends React.Component {
  render() {
    return (
      <>
        <div className="container">
          <div className="row m-section">
            <div className="col-span-12 sm:col-span-10 sm:col-start-3">
              <h1>
                <DoubleHeader hero smallTitle="A look into our" largeTitle="Frequently Asked Questions" />
              </h1>
            </div>
          </div>
        </div>
        <div className="bg-gray-lighter">
          <div className="container">
            <div className="row m-section">
              <div className="col-span-12 mb-32 xs:col-span-4 sm:col-span-5 md:col-span-4 lg:col-span-3">
                <div className="sticky top-0 space-y-4 bg-white p-16 xs:p-32">
                  <h3 className="text-h4 mb-8">Browse all topics</h3>

                  <Line />

                  <p>
                    <a className="tracker-nav-faq_page-sidebar-20190121-144011" href="#anchor-link-how_do">
                      How do tuxedo rentals work
                    </a>
                  </p>

                  <p>
                    <a className="tracker-nav-faq_page-sidebar-20190121-144016" href="#anchor-link-measurements">
                      Measurements & Fit
                    </a>
                  </p>

                  <p>
                    <a className="tracker-nav-faq_page-sidebar-20190121-144019" href="#anchor-link-payment">
                      Payment
                    </a>
                  </p>

                  <p>
                    <a className="tracker-nav-faq_page-sidebar-20190121-144023" href="#anchor-link-shipping">
                      Shipping
                    </a>
                  </p>

                  <p>
                    <a className="tracker-nav-faq_page-sidebar-20190121-144027" href="#anchor-link-returns">
                      Returns & Cancellations
                    </a>
                  </p>

                  <p>
                    <a className="tracker-nav-faq_page-sidebar-20190121-144030" href="#anchor-link-garment_care">
                      Garment Care
                    </a>
                  </p>

                  <p>
                    <a className="tracker-nav-faq_page-sidebar-20190121-144033" href="#anchor-link-try_on">
                      Home Try-on
                    </a>
                  </p>

                  <p>
                    <a className="tracker-nav-faq_page-sidebar-20190121-144037" href="#anchor-link-weddings">
                      Wedding Parties
                    </a>
                  </p>
                </div>
              </div>

              <div className="col-start-1 col-end-13 space-y-32 xs:col-start-5 sm:col-start-6 sm:space-y-64 lg:col-start-5 lg:col-end-12">
                <div id="anchor-link-how_do">
                  <FaqGroup title="How do tuxedo rentals work?">
                    <FaqItem question="How do I rent from Menguin?" trackerClass="tracker-btn-faq-how_to_rent-220117-144838">
                      <p>
                        We think you will find our rental experience the fastest and easiest way to rent a suit or tuxedo online. Here is
                        the proven recipe:
                      </p>

                      <ol>
                        <li>
                          Shop the site to{' '}
                          <Link href="/collection/[category]" as="/collection/tuxedos-and-suits">
                            create your look
                          </Link>
                          .
                        </li>

                        <li>Enter details about your Fit Profile and check out.</li>

                        <li>Sit tight - your order will arrive via UPS about fourteen (14) days before your event.</li>

                        <li>
                          Try on your garments within 48 hours of delivery and&nbsp;
                          <Link href="/support">contact us</Link> with any questions or concerns.
                        </li>

                        <li>
                          Within three (3) days following your event, using the prepaid return bag, send your rental items back to us via
                          UPS.
                        </li>
                      </ol>
                    </FaqItem>

                    <FaqItem question="Are you online only? Convince me." trackerClass="tracker-btn-faq-online_only-220117-144838">
                      <p>
                        OK, here’s the deal. We are online only, meaning we do not have brick and mortar locations. Why is this good for
                        you? It reduces overhead and enables us to price the rental experience to be a terrific value for our customers.
                      </p>

                      <p>Here’s our value proposition. We save you time and offer you a high-quality product at the best possible price.</p>

                      <p>
                        It’s easier than going to a store. We are more convenient than our brick and mortar competitors, and we save you
                        time. Renting a suit or tuxedo from a store requires a minimum of three (3) trips: one trip to get measured, one
                        trip to pick up the rental, and one trip to return the rental. With Menguin, you book online at your convenience,
                        your rental arrives fast and ships anywhere in the United States, and you ship it back to us on our dime - one trip!
                      </p>

                      <p>
                        Our quality is the best - our garments are nicer than our competitors’. We have designed and tailored our garments
                        to represent the highest quality rental{' '}
                        <Link href="/collection/[category]" as="/collection/tuxedos-and-suits">
                          suits and tuxedos
                        </Link>{' '}
                        possible. Why? Because George Zimmer (Founder, CEO) wants you to look great on your special day.
                      </p>

                      <p>
                        Our pricing is competitive. See for yourself - price a suit or tuxedo rental with the same quality of Menguin
                        elsewhere, and you will find that we are the best value in both price and quality.
                      </p>

                      <p>If there are fit issues, we will send free replacement garment(s). </p>

                      <p>
                        Test-drive your rental with our free <Link href="/app/hto/looks/build">home try-on</Link>. For grooms and wedding
                        couples, we offer a {HTO_RETURN_WINDOW_IN_HOURS}-hour, no risk, free home try-on to demonstrate the experience, fit,
                        and quality of our garments.
                      </p>

                      <p>
                        Free rental for the wedding couple! If you have 5 or more people renting a complete suit or tux outfit (valued at
                        $190), the groom or wedding couple qualifies for one free outfit rental.
                      </p>
                    </FaqItem>

                    <FaqItem question="What is the quality of your garments?" trackerClass="tracker-btn-faq-garment_quality-220117-144838">
                      <p>
                        In order to achieve our high standards for fabric, fit, and price, we designed and manufactured our own exclusive
                        brand, Seven–26, created by our founder and CEO George Zimmer.
                      </p>

                      <p>
                        The luxurious, in-hand feel of premium wool in all our&nbsp;
                        <Link href="/collection/[category]" as="/collection/tuxedos-and-suits">
                          suits and tuxedos
                        </Link>{' '}
                        delivers a natural drape that’s flattering in ways lower-grade wool and poly-blends can’t replicate. Our&nbsp;
                        <Link href="/collection/[category]" as="/collection/tuxedos-and-suits">
                          tuxedos
                        </Link>{' '}
                        feature 100% super 130s–140s wool, satin-faced/piped lapels, and a bespoke lining story, in slim or modern fits. Our
                        suits feature 100% super 120s–130s wool, side vents, and a bespoke lining story, in slim and modern fits. All of our
                        shirts feature premium materials, including 100% cotton and performance microfiber, to keep you cool and comfortable
                        at all times.
                      </p>
                    </FaqItem>

                    <FaqItem question="May I rent individual items?" trackerClass="tracker-btn-faq-individual_items-220117-144838">
                      <p>
                        Of course! Any single item on our site may be rented individually, except for jackets and pants, which are only
                        available as a matched set. Individual items are subject to shipping. Please <Link href="/support">contact us</Link>{' '}
                        for more details.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="How far in advance should I book my rental?"
                      trackerClass="tracker-btn-faq-rental_advance-220117-144838"
                    >
                      <p>
                        As soon as you know your event date. If you need to rent for an event that is fewer than sixteen (16) days (21 days
                        for AK, HI) from the time of order, please <Link href="/support">contact us</Link> for assistance in setting up your
                        event.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="Where can I find the status of my rental order?"
                      trackerClass="tracker-btn-faq-order_status-220117-144838"
                    >
                      <p>
                        Once you have checked out, you can open an event at any time to see the reservation status dashboard for your
                        account. If you have multiple people in your event, you can see the status of their orders as well. Our reservation
                        management system will also automatically send email updates to you and other members of your event!
                      </p>
                    </FaqItem>

                    <FaqItem question="When will my rental order arrive?" trackerClass="tracker-btn-faq-order_arrival-220117-144838">
                      <p>
                        We strive to get your order to you about fourteen (14) days before your event; however, garment arrival times may
                        vary during our peak seasons. When your order arrives, please open the package immediately and try on your garments.
                        If you encounter any issues, please&nbsp;
                        <Link href="/support">contact us</Link> within 48 hours so that we will have time to make any corrections and avoid
                        rush shipping fees.
                      </p>
                    </FaqItem>

                    <FaqItem question="How do I return my rental?" trackerClass="tracker-btn-faq-rental_return-220117-144838">
                      <p>
                        You will receive a prepaid return shipping label with your package. Within three (3) days following your event, send
                        your rental items back to us via UPS using the prepaid return bag.
                      </p>
                    </FaqItem>

                    <FaqItem question="Can I purchase rental items?" trackerClass="tracker-btn-faq-rental_purchase-220117-144838">
                      <p>
                        No. We do have retail accessories for purchase, but at this time rental items are not available for retail purchase.
                        Retail items are noted with a “Buy Now” button under the item description.
                      </p>
                    </FaqItem>
                  </FaqGroup>
                </div>

                <div id="anchor-link-measurements">
                  <FaqGroup title="Measurements & Fit">
                    <FaqItem question="How do I get measured?" trackerClass="tracker-btn-faq-measurements-220117-144838">
                      <p>
                        All we need are a few answers to questions about your body profile. We also ask for age, height, and weight, along
                        with shoe and jean size. Using our proprietary fit technology, eTailor, we will determine the right fit for you.
                      </p>

                      <p>
                        For customers age fifteen (15) and under, we will ask for more specific measurements. All you need is a tape measure
                        and a friend. If you don’t have a tape measure, don’t worry, you can request one while filling out the Fit Profile.
                        Just follow our step-by-step guide and save the measurements on your profile.
                      </p>
                    </FaqItem>

                    <FaqItem question="How do I get a free tape measure?" trackerClass="tracker-btn-faq-free_tape_measure-220117-144838">
                      <p>
                        With our new eTailor fit technology, you no longer need a tape measure if you are sixteen (16) years of age or over.
                        For event members age fifteen (15) and under, simply create an account, complete the Fit Profile information, and
                        then click the “Order Tape Measure” button on the measurements page to order a complimentary tape measure. We will
                        take care of the rest. Tape measures are shipped via USPS.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="How do I make changes to my Fit Profile once I’ve submitted an order?"
                      trackerClass="tracker-btn-faq-fit_profile_changes-220117-144838"
                    >
                      <p>
                        You can log into your account to update your Fit Profile up to thirty (30) days before your event. If you need to
                        change your Fit Profile and your event is fewer than thirty (30) days away, please{' '}
                        <Link href="/support">contact us</Link> for assistance.
                      </p>
                    </FaqItem>

                    <FaqItem question="What sizes do you offer?" trackerClass="tracker-btn-faq-size_offerings-220117-144838">
                      <p>
                        We offer sizes 3T through men’s 66L in select styles. Our slim fit styles are available up to size 46XL in our
                        jackets and vests. We offer slim pant styles up to a 40" waist.{' '}
                      </p>
                    </FaqItem>

                    <FaqItem question="What if a garment does not fit?" trackerClass="tracker-btn-faq-does_not_fit-220117-144838">
                      <p>
                        When you receive your outfit, please open the package and try on your garments immediately, within 48 hours of
                        delivery. If you feel any item in your package does not fit properly, please call us immediately, and we will
                        determine the best method for handling alterations or exchanges. We have found photos of you wearing the garments
                        work best to help us accurately assess any fit issue(s) and quickly resolve them.
                      </p>

                      <p>
                        For fit issues related to pant or jacket sleeve length, we can authorize you for complimentary tailoring services.
                        This means you can go to your local tailor and we will gladly reimburse up to $20.00 for pant hem or a jacket sleeve
                        adjustment. All other alterations to our rental garments are prohibited, are not reimbursable, and are subject to
                        replacement fees.
                      </p>

                      <p>
                        For sizing issues related to the fit of a jacket, pants, shirt, vest, belt, or shoes, we can send you a replacement
                        item.
                      </p>

                      <p>
                        Fit concerns that are reported beyond 48 hours of UPS delivering your outfit may be subject to additional shipping
                        fees to expedite delivery of any replacement garments.
                      </p>

                      <p>All items can be returned together in the original bag using the included prepaid shipping label.</p>
                    </FaqItem>

                    <FaqItem
                      question="Do your pants have an adjustable waistband?"
                      trackerClass="tracker-btn-faq-adjustable_waistband-220117-144838"
                    >
                      <p>
                        Our pants are designed with a hidden Flex Fit waistband that will automatically expand up to one (1) inch on both
                        sides if needed.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="Are all of your pants able to be worn with a belt or suspenders?"
                      trackerClass="tracker-btn-faq-belt_suspenders-220117-144838"
                    >
                      <p>
                        Our suit pants have belt loops and accept a belt; our tuxedo pants do not. While our pants do not have buttons for
                        suspenders, they do accept clip-on suspenders.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="Are the shirt collars adjustable?"
                      trackerClass="tracker-btn-faq-adjustable_shirt_collars-220117-144838"
                    >
                      <p>Our shirts are designed with a Flex Fit collar neck and will expand up to one (1) inch if needed.</p>
                    </FaqItem>
                  </FaqGroup>
                </div>

                <div id="anchor-link-payment">
                  <FaqGroup title="Payment">
                    <FaqItem question="Is there a deposit fee?" trackerClass="tracker-btn-faq-deposit_fee-220117-144838">
                      <p>No, we do not collect a deposit fee at this time.</p>

                      <p>If you check out less than sixteen (16) days before an event, rush shipping and processing could apply.</p>

                      <p>
                        Fit concerns that are reported more than 48 hours after your outfit is delivered by UPS are subject to additional
                        shipping fees to expedite delivery of the replacement shipment. Please <Link href="/support">contact us</Link> for
                        assistance with rush order requests.
                      </p>
                    </FaqItem>

                    <FaqItem question="When do I pay for my order?" trackerClass="tracker-btn-faq-when_to_pay-220117-144838">
                      <p>All rentals need to be paid for in full at the time of checkout.</p>
                    </FaqItem>

                    <FaqItem question="May I pay for someone else in my event?" trackerClass="tracker-btn-faq-pay_for_others-220117-144838">
                      <p>
                        Yes. As the event owner/organizer, you have the option of paying for the entire group or for select members of the
                        group during checkout.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="What credit cards do you accept?"
                      trackerClass="tracker-btn-faq-credit_cards_acceptance-220117-144838"
                    >
                      <p>We accept Visa, MasterCard, AMEX, and Discover.</p>
                    </FaqItem>

                    <FaqItem
                      question="When do you charge my credit card?"
                      trackerClass="tracker-btn-faq-when_is_card_charged-220117-144838"
                    >
                      <p>
                        Payment (including tax, where required) is due in full at time of checkout. You may see a transaction pending
                        authorization that is typical of credit card processing. This authorization is temporary and usually clears within
                        one (1) to three (3) business days.
                      </p>
                    </FaqItem>
                  </FaqGroup>
                </div>

                <div id="anchor-link-shipping">
                  <FaqGroup title="Shipping">
                    <FaqItem question="How do you ship?" trackerClass="tracker-btn-faq-shipping_type-220117-144838">
                      <p>
                        We use UPS to ensure the highest quality of delivery and return service. Packages arrive via UPS and must be
                        returned using UPS. If you decide to use a different carrier to return your rental, you will not be reimbursed for
                        shipping costs. Packages are shipped without a required signature and are typically left at the door at most
                        locations. All free shipping is ground shipping only. Please <Link href="/support">contact us</Link> for assistance
                        to discuss whether upgraded shipping options are available.
                      </p>
                    </FaqItem>

                    <FaqItem question="Where do you ship?" trackerClass="tracker-btn-faq-shipping_locations-220117-144838">
                      <p>
                        We only ship to addresses in the United States, including Alaska and Hawaii. We do not ship to P.O. boxes or APO/FPO
                        addresses, and we do not ship internationally. No exceptions.
                      </p>
                    </FaqItem>

                    <FaqItem question="Where should I have my rental shipped?" trackerClass="tracker-btn-faq-where_to_ship-220117-144838">
                      <p>
                        You should have your order shipped to the location you will be fourteen (14) days prior to your event. If you have
                        concerns or questions about where to have your order shipped, please <Link href="/support">contact us</Link> - we
                        would be happy to discuss shipping strategies. Do not have your order shipped to an address that does not allow for
                        packages to be left at the door.
                      </p>
                    </FaqItem>

                    <FaqItem question="How can I qualify for free shipping?" trackerClass="tracker-btn-faq-free_shipping-220117-144838">
                      <p>
                        Ground shipping is free with a minimum order of a tuxedo/suit; otherwise, shipping is $25.00 per shipping address.
                        Should your order require shipping a replacement garment, ground shipping is free so long as you{' '}
                        <Link href="/support">contact us</Link> within 48 hours of receiving your original package to report any fit issues.
                        Fit concerns that are reported beyond 48 hours that require replacement garments to be sent could be subject to rush
                        shipping and processing.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="I lost my shipping return label, how do I get another one?"
                      trackerClass="tracker-btn-faq-return_label_replacement-220117-144838"
                    >
                      <p>
                        Hey, things happen. Just <Link href="/support">contact us</Link> and we will email you a complimentary shipping
                        label.
                      </p>
                    </FaqItem>

                    <FaqItem question="How do I return my rental?" trackerClass="tracker-btn-faq-rental_return-220117-144838">
                      <p>
                        All you need to do is place all rental items in the prepaid return bag included with your order, and drop it off at
                        your nearest UPS Store location within three (3) days after your event.
                      </p>

                      <p>What if I need to change the shipping address after I’ve submitted my order?</p>

                      <p>
                        Please <Link href="/support">contact us</Link> no later than sixteen (16) days before the event date and we will
                        update the shipping address for you. Keep in mind that your package will arrive about fourteen (14) days before your
                        event date, so the sooner you let us know of any shipping address changes the sooner we can make changes to ensure
                        accurate and timely delivery.
                      </p>
                    </FaqItem>
                  </FaqGroup>
                </div>

                <div id="anchor-link-returns">
                  <FaqGroup title="Returns & Cancellations">
                    <FaqItem
                      question="What if I change my mind and decide to switch the garment selections I’ve made for my group?"
                      trackerClass="tracker-btn-faq-switch_garment_selections-220117-144838"
                    >
                      <p>
                        You can change garment selections in your account up to sixteen (16) days prior to the event for any unpaid orders.
                        If the event is less than sixteen (16) days away or if payment has already been taken for an order, please{' '}
                        <Link href="/support">contact us</Link> and we will be happy to assist you in updating your selections.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="What if I want to change my rental choices after shipping?"
                      trackerClass="tracker-btn-faq-changes_after_shipping-220117-144838"
                    >
                      <p>
                        Please <Link href="/support">contact us</Link> to discuss options for style changes after an order has been shipped.
                        Style swap fees may apply.
                      </p>
                    </FaqItem>

                    <FaqItem question="When do I need to return my rental?" trackerClass="tracker-btn-faq-return_window-220117-144838">
                      <p>
                        Place all of your rental items in the prepaid return bag included with your order and drop it off at your nearest
                        UPS store within three (3) days following your event to avoid late and/or replacement fees.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="May I keep my rental longer than the three days allowed after my event?"
                      trackerClass="tracker-btn-faq-late_fee-220117-144838"
                    >
                      <p>
                        There is a late fee of $25.00 per day for each rental kept longer than three (3) days after the event. If you know
                        in advance that you will need the rental order longer than the time allotted, please{' '}
                        <Link href="/support">contact us</Link> to make special delivery/return arrangements. We offer extended rental fixed
                        fees.{' '}
                      </p>
                    </FaqItem>

                    <FaqItem question="What do I do if my event is canceled?" trackerClass="tracker-btn-faq-event_cancelled-220117-144838">
                      <p>
                        You may cancel your order up to 3 weeks (21 days) prior to your event date and receive a full refund of the rental
                        fee. If your order has shipped, a credit will be issued if the unused garments are returned to UPS prior to your
                        event date. Please note, if you have not cancelled your order within 3 weeks (21 days) of your event date there will
                        be a $40 shipping and restocking fee.
                      </p>
                    </FaqItem>
                  </FaqGroup>
                </div>

                <div id="anchor-link-garment_care">
                  <FaqGroup title="Garment Care">
                    <FaqItem
                      question="How do I care for my garments once I receive them?"
                      trackerClass="tracker-btn-faq-garment_care-220117-144838"
                    >
                      <p>
                        To ensure that you look sharp for your big day, make sure that after receiving your garments and trying them on, you
                        hang up all garments to avoid any wrinkles. If necessary, you can use steam to remove wrinkles (using a fabric
                        steamer or just a steamy bathroom). Do not iron the garments. If you are experiencing more than minor wrinkling,
                        please <Link href="/support">contact us</Link> for assistance. We can authorize you for complimentary pressing
                        services. This means that you can go to your local dry cleaner, and we will happily reimburse you for up to $20 for
                        pressing.{' '}
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="Do I need to have my rental items cleaned before shipping them back?"
                      trackerClass="tracker-btn-faq-cleaning-220117-144838"
                    >
                      <p>
                        Of course not. Simply ship all garments back to us after the event and we’ll take care of all cleaning at our state
                        of the art facilities where all garments are thoroughly inspected and cleaned. All dry cleaning is done with the
                        environmentally friendly GreenEarth® cleaning process.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="Will I be charged if there is any damage to my rental?"
                      trackerClass="tracker-btn-faq-damage_charges-220117-144838"
                    >
                      <p>
                        If we can restore the garment to our quality standards using our in-house tailors and dry cleaning facility, we will
                        do so, free of charge. If the garment is considered damaged beyond repair or destroyed, you may be charged a
                        replacement fee. This damage is assessed by the returns department, and fees may vary.{' '}
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="Will I be charged if I lose an item or it doesn’t ultimately get returned?"
                      trackerClass="tracker-btn-faq-lost_item-220117-144838"
                    >
                      <p>
                        If you return your items and something is missing, we will contact you to see if you still have the missing items.
                        If you do, we will help arrange for their return. If you returned all items to an authorized UPS location with the
                        return label applied but the shipment was lost in transit, you will not be charged for the lost items. If any items
                        are truly lost or destroyed, you will be charged a replacement fee for these items. The following is an itemized
                        list of replacement fees:
                      </p>

                      <ul>
                        <li>Jacket: $600.00</li>

                        <li>Pants: $200.00</li>

                        <li>Vest: $150.00</li>

                        <li>Shirt: $100.00</li>

                        <li>Tie: $100.00</li>

                        <li>Cummerbund: $100.00</li>

                        <li>Shoes: $200.00</li>

                        <li>Belt: $100.00</li>

                        <li>Cufflinks and Studs: $50.00</li>
                      </ul>
                    </FaqItem>

                    <FaqItem question="What is the damage waiver charge?" trackerClass="tracker-btn-faq-damage_waiver-220117-144838">
                      <p>
                        The damage waiver charge is a $10.00 nonrefundable fee that covers the cost of a quality control inspection, minor
                        repairs, and cleaning related to normal use of the rental garments. In other words, you don’t have to sweat the
                        small stuff. That said, if a rental garment is destroyed beyond repair, you may be charged a replacement fee. See
                        our rental agreement for details.
                      </p>
                    </FaqItem>
                  </FaqGroup>
                </div>

                <div id="anchor-link-try_on">
                  <FaqGroup title="Home Try-on">
                    <FaqItem question="Do you offer suit or tuxedo home try-on?" trackerClass="tracker-btn-faq-hto-220117-144838">
                      <p>
                        For our grooms and wedding couples, we offer a {HTO_RETURN_WINDOW_IN_HOURS}-hour, no risk, free{' '}
                        <Link href="/app/hto/looks/build">Home Try-On</Link> to demonstrate the experience, fit, and quality of our
                        garments. Click here to order your <Link href="/app/hto/looks/build">home try-on</Link> today or contact us!
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="How much does it cost to receive a suit or tuxedo home try-on?"
                      trackerClass="tracker-btn-faq-hto_cost-220117-144838"
                    >
                      <p>
                        Nothing at all! Our <Link href="/app/hto/looks/build">Home Try-On Program</Link> is free to grooms and wedding
                        couples. We do require a valid credit or debit card for your <Link href="/app/hto/looks/build">home try-on</Link>{' '}
                        order, but not to worry - the service is still free! We authorize your card for the value of the garments, but you
                        will not be charged unless you do not return the outfit. Click here to order your{' '}
                        <Link href="/app/hto/looks/build">home try-on</Link> today, or <Link href="/support">contact us</Link>!
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="What is included in the home try-on?"
                      trackerClass="tracker-btn-faq-hto_included_items-220117-144838"
                    >
                      <p>
                        The <Link href="/app/hto/looks/build">Home Try-On</Link> includes a jacket, pants, vest/cummerbund, shirt, tie,
                        shoes, and free ground shipping both ways.
                      </p>

                      <p>
                        Complimentary tailor services and shipping item replacements are not available during the{' '}
                        <Link href="/app/hto/looks/build">home try-on</Link>. Actual styles included in the{' '}
                        <Link href="/app/hto/looks/build">home try-on</Link> are dependent on item availability; this can vary during peak
                        season.
                      </p>
                    </FaqItem>

                    <FaqItem question="When will my home try-on order arrive?" trackerClass="tracker-btn-faq-hto_arrival-220117-144838">
                      <p>
                        All of our <Link href="/app/hto/looks/build">home try-ons</Link> are shipped to arrive as soon as possible. Due to
                        popular demand for some of our products, certain items may not be available right away. Please call our Customer
                        Experience Team (<a href="tel:1-844-636-4846">844.636.4846</a>) if you have any questions about specific time frame
                        requests or styles. <Link href="/app/hto/looks/build">Home try-on</Link> orders cannot be shipped to arrive on a
                        weekend date.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="When does my home-try on order need to be returned?"
                      trackerClass="tracker-btn-faq-hto_return-220117-144838"
                    >
                      <p>
                        Place the included prepaid shipping label on your original bag and drop it off at your nearest UPS store no later
                        than {HTO_RETURN_WINDOW_IN_HOURS} hours after delivery to avoid late fees. Due to the demand for{' '}
                        <Link href="/app/hto/looks/build">home try-on</Link> product, home try-on orders may not be kept beyond the{' '}
                        {HTO_RETURN_WINDOW_IN_HOURS}-hour window.
                      </p>
                    </FaqItem>

                    <FaqItem question="May I have multiple home try-on orders?" trackerClass="tracker-btn-faq-hto_multiple-220117-144838">
                      <p>
                        Due to the demand for home try-on product, home try-on orders are limited to one per wedding couple. If you and your
                        partner will both be renting a suit or tuxedo for your wedding, please contact customer experience to request an
                        additional home try-on.
                      </p>
                    </FaqItem>
                  </FaqGroup>
                </div>

                <div id="anchor-link-weddings">
                  <FaqGroup title="Wedding Parties">
                    <FaqItem question="Can you match my wedding colors?" trackerClass="tracker-btn-faq-color_match-220117-144838">
                      <p>
                        We have a variety of colors to match or complement the colors of your wedding. If you need a style consultation,
                        please <Link href="/support">contact us</Link> - we are only a phone call away!
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="May I receive a fabric swatch prior to placing my rental order?"
                      trackerClass="tracker-btn-faq-swatches-220117-144838"
                    >
                      <p>
                        You bet! We offer complimentary swatches for our customers.{' '}
                        <Link href="/app/swatch/build?swatches">Order your swatches today</Link>.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="What if I’m having a destination wedding?"
                      trackerClass="tracker-btn-faq-destination_wedding-220117-144838"
                    >
                      <p>
                        Please <Link href="/support">contact us</Link> to discuss logistics around special delivery/return arrangements. We
                        can work out an affordable extended rental that suits your travel constraints and avoids late fees.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="May I set up an event only for wedding party members?"
                      trackerClass="tracker-btn-faq-events-220117-144838"
                    >
                      <p>
                        Of course! Our event management platform makes it easy for you to set up an event and assign as many or as few roles
                        as you’d like. Start by creating a new event and selecting “Wedding” as your event type; then choose your role in
                        the event. Next, create a wardrobe for the wedding party and enter their contact information. Finally, invite guests
                        and follow the instructions - our system will take care of the rest. You can revisit the event at any time to check
                        the status of your order and monitor the progress of each event member.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="How do I register members of my wedding party?"
                      trackerClass="tracker-btn-faq-registration-220117-144838"
                    >
                      <p>
                        When you create an event, be sure to choose “Wedding“ as the event type. Within each wardrobe, simply add their
                        contact information, including name (first and last), email, and phone number. To invite your event members, we will
                        send an electronic invitation to each member, allowing them to create their own account, complete their Fit Profile,
                        and ultimately receive the outfit you created for them. Please advise your event members to use the email address
                        where they received the invitation to log in to ensure that they do not create a duplicate account by mistake.
                      </p>
                    </FaqItem>

                    <FaqItem
                      question="May I change or delete a member in my wedding party after I’ve already checked out?"
                      trackerClass="tracker-btn-faq-change_members-220117-144838"
                    >
                      <p>
                        Party members can easily be added or changed up to sixteen (16) days before your event by visiting your event
                        details page in your account and adding their contact information to the wardrobe/role you would like them to
                        select. If you need to make a change to or remove an event member inside of sixteen (16) days from the event or
                        after the member has paid, please contact us for assistance.
                      </p>
                    </FaqItem>
                  </FaqGroup>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="row">
            <div className="col-start-1 col-end-13 xs:col-start-5 sm:col-start-6 md:col-start-6 lg:col-start-5">
              <div>
                <h2 className="text-h1-display mb-16">Still need help?</h2>

                <Link href="/support" className="btn-info tracker-cta-faq_page-support-20190121-143601 btn">
                  Visit Customer Experience
                </Link>
              </div>

              <div>
                <h3 className="text-h4 mb-8 mt-32 text-gray-dark">Holiday Schedule</h3>

                <p>New Years Day – Closed</p>

                <p>Easter – Closed</p>

                <p>Independence Day – Closed</p>

                <p>Thanksgiving – Closed</p>

                <p>Christmas – Closed</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gray-lighter">
          <NextSteps
            ctaPageName="faq"
            salesCallout1={<SalesCalloutHTO ctaPageName="faq" />}
            salesCallout2={<SalesCalloutSwatches ctaPageName="faq" />}
          />
        </div>
      </>
    );
  }
}

export const getStaticProps = withPageStaticProps();

export default FAQ;
